<template>
  <div class="doctor-booking-page">
    <b-card
      no-body
      class="main-card"
    >
      <div class="page-container">

        <!-- Header -->
        <b-row class="header-row">
          <b-col
            md="12"
            class="d-flex justify-content-between align-items-center"
          >
            <div class="page-title-block">
              <span class="title-icon">
                <feather-icon
                  icon="CalendarIcon"
                  size="20"
                />
              </span>
              <h2 class="mb-0 page-title">
                {{ $t("surgery.doctor-booking") }}
              </h2>
            </div>
            <div class="view-toggle-buttons">
              <b-button-group size="sm">
                <b-button
                  :variant="viewMode === 'cards' ? 'primary' : 'outline-secondary'"
                  size="sm"
                  class="toggle-btn"
                  @click="viewMode = 'cards'"
                >
                  <feather-icon
                    icon="GridIcon"
                    size="13"
                    class="mr-50"
                  />
                  {{ $t("common.card_view") }}
                </b-button>
                <b-button
                  :variant="viewMode === 'list' ? 'primary' : 'outline-secondary'"
                  size="sm"
                  class="toggle-btn"
                  @click="viewMode = 'list'"
                >
                  <feather-icon
                    icon="ListIcon"
                    size="13"
                    class="mr-50"
                  />
                  {{ $t("common.list_view") }}
                </b-button>
              </b-button-group>
            </div>
          </b-col>
        </b-row>

        <!-- Filters -->
        <b-row class="filters-row">
          <b-col
            md="3"
            cols="12"
          >
            <b-form-group
              :label="$t('common.date')"
              label-for="booking_filter_date"
              class="filter-form-group"
            >
              <datepicker
                id="booking_filter_date"
                v-model="filterDate"
                :calendar-button="true"
                :clear-button="true"
                :bootstrap-styling="true"
                class="datebtn2"
                placeholder="YYYY-MM-DD"
              />
            </b-form-group>
          </b-col>

          <!-- Status summary chips (shown when data loaded) -->
          <b-col
            v-if="!loading && bookings.length"
            md="9"
            cols="12"
            class="d-flex align-items-end pb-1"
          >
            <div class="status-summary">
              <span
                v-for="s in statusSummary"
                :key="s.key"
                :class="['summary-chip', `summary-chip--${s.key}`]"
              >
                {{ s.label }}: <strong>{{ s.count }}</strong>
              </span>
            </div>
          </b-col>
        </b-row>

        <!-- Content -->
        <b-row class="content-row">
          <b-col>

            <!-- Loading -->
            <div
              v-if="loading"
              class="loading-state"
            >
              <b-spinner variant="primary" />
              <p class="loading-text">
                {{ $t("common.loading") }}
              </p>
            </div>

            <!-- List View -->
            <b-table
              v-else-if="viewMode === 'list'"
              :items="bookings"
              :fields="bookingColumns"
              primary-key="id"
              responsive
              show-empty
              :empty-text="$t('common.no_data')"
              class="booking-table"
              tbody-tr-class="booking-table-row"
            >
              <template #cell(patient)="row">
                <div
                  class="d-flex align-items-center"
                  style="gap: 8px;"
                >
                  <div class="patient-avatar">
                    {{ getInitials(getPatientCardName(row.item.patient_card)) }}
                  </div>
                  <span class="patient-name-text">{{ getPatientCardName(row.item.patient_card) }}</span>
                </div>
              </template>

              <template #cell(doctor)="row">
                <span class="doctor-name-text">{{ getDoctorName(row.item.doctor) }}</span>
              </template>

              <template #cell(consultation_date)="row">
                <div class="date-cell">
                  <feather-icon
                    icon="ClockIcon"
                    size="12"
                    class="mr-25 text-muted"
                  />
                  {{ formatConsultationDateDisplay(row.item.consultation_date) }}
                </div>
              </template>

              <template #cell(status)="row">
                <div
                  class="d-flex align-items-center"
                  style="gap: 8px;"
                >
                  <span :class="['status-pill', `status-pill--${row.item.status}`]">
                    <span class="status-dot" />
                    {{ statusLabel(row.item.status) }}
                  </span>
                  <b-form-select
                    v-model="row.item.status"
                    :options="statusSelectOptions"
                    size="sm"
                    :disabled="updatingStatusId === row.item.id"
                    style="width: 130px;"
                    @change="updateStatus(row.item)"
                  />
                  <b-spinner
                    v-if="updatingStatusId === row.item.id"
                    small
                    variant="primary"
                  />
                </div>
              </template>

              <template #cell(actions)="row">
                <b-button
                  v-if="canShowPatientCard && patientCardNavParams(row.item)"
                  pill
                  size="sm"
                  variant="outline-primary"
                  @click.stop="goToPatientCard(row.item)"
                >
                  <feather-icon
                    icon="UserIcon"
                    size="12"
                    class="mr-25"
                  />
                  {{ $t("reception.see-active-card") }}
                </b-button>
                <span
                  v-else-if="!patientCardNavParams(row.item)"
                  class="text-muted small"
                >—</span>
              </template>
            </b-table>

            <!-- Cards View -->
            <div
              v-else
              class="cards-container"
            >

              <!-- Empty state -->
              <div
                v-if="bookings.length === 0"
                class="empty-state"
              >
                <div class="empty-icon-wrap">
                  <feather-icon
                    icon="CalendarIcon"
                    size="40"
                  />
                </div>
                <h5 class="empty-title">
                  {{ $t("common.no-results") }}
                </h5>
                <p class="empty-sub text-muted">
                  <span v-if="filterDate">
                    {{ $t("common.no-data-for-date") }}: {{ formatDate(filterDate) }}
                  </span>
                  <span v-else>{{ $t("common.no-matching-results-found") }}</span>
                </p>
              </div>

              <!-- Booking cards -->
              <b-row v-else>
                <b-col
                  v-for="item in bookings"
                  :key="item.id"
                  cols="12"
                  md="6"
                  lg="4"
                  class="card-col"
                >
                  <div :class="['booking-card', `booking-card--${item.status}`]">

                    <!-- Card top accent bar -->
                    <div :class="['card-accent-bar', `bar--${item.status}`]" />

                    <!-- Card Header -->
                    <div class="booking-card-header">
                      <div
                        class="d-flex align-items-center"
                        style="gap: 10px;"
                      >
                        <div :class="['booking-avatar', `avatar--${item.status}`]">
                          {{ getInitials(getPatientCardName(item.patient_card)) }}
                        </div>
                        <div>
                          <div class="booking-patient-name">
                            {{ getPatientCardName(item.patient_card) }}
                          </div>
                          <div class="booking-card-id text-muted">
                            #{{ item.id }}
                          </div>
                        </div>
                      </div>
                      <span :class="['booking-status-badge', `badge--${item.status}`]">
                        <span class="badge-dot" />
                        {{ statusLabel(item.status) }}
                      </span>
                    </div>

                    <!-- Card Body -->
                    <div class="booking-card-body">
                      <div class="info-row">
                        <feather-icon
                          icon="UserCheckIcon"
                          size="13"
                          class="info-icon"
                        />
                        <span class="info-label">{{ $t("booking.doctor") }}</span>
                        <span class="info-value">{{ getDoctorName(item.doctor) }}</span>
                      </div>

                      <div class="info-row">
                        <feather-icon
                          icon="ClockIcon"
                          size="13"
                          class="info-icon"
                        />
                        <span class="info-label">{{ $t("booking.consultation_date") }}</span>
                        <span class="info-value">{{ formatConsultationDateDisplay(item.consultation_date) }}</span>
                      </div>

                      <div class="info-row status-select-row">
                        <feather-icon
                          icon="RefreshCwIcon"
                          size="13"
                          class="info-icon"
                        />
                        <span class="info-label">{{ $t("booking.status") }}</span>
                        <div
                          class="d-flex align-items-center"
                          style="gap: 6px; flex: 1;"
                        >
                          <b-form-select
                            v-model="item.status"
                            :options="statusSelectOptions"
                            size="sm"
                            :disabled="updatingStatusId === item.id"
                            class="status-select"
                            @change="updateStatus(item)"
                          />
                          <b-spinner
                            v-if="updatingStatusId === item.id"
                            small
                            variant="primary"
                          />
                        </div>
                      </div>
                    </div>

                    <!-- Card Footer -->
                    <div class="booking-card-footer">
                      <b-button
                        v-if="canShowPatientCard && patientCardNavParams(item)"
                        block
                        size="sm"
                        :variant="statusButtonVariant(item.status)"
                        class="card-action-btn"
                        @click="goToPatientCard(item)"
                      >
                        <feather-icon
                          icon="UserIcon"
                          size="13"
                          class="mr-50"
                        />
                        {{ $t("reception.see-active-card") }}
                      </b-button>
                      <div
                        v-else
                        class="no-card-placeholder"
                      >
                        —
                      </div>
                    </div>

                  </div>
                </b-col>
              </b-row>
            </div>

          </b-col>
        </b-row>

        <!-- Pagination -->
        <b-row class="pagination-row">
          <b-col
            cols="12"
            class="d-flex flex-wrap justify-content-between align-items-center"
          >
            <div class="pagination-info">
              <span class="text-muted small">{{ paginationSummary }}</span>
            </div>
            <b-pagination
              v-model="page"
              :total-rows="dataCount"
              :per-page="take"
              first-number
              last-number
              size="sm"
              class="mb-0"
            />
            <b-form-select
              v-model="take"
              :options="takeOptions"
              size="sm"
              style="width: auto; min-width: 5rem;"
              @change="onTakeChange"
            />
          </b-col>
        </b-row>

      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BRow,
  BCol,
  BTable,
  BButton,
  BButtonGroup,
  BFormGroup,
  BFormSelect,
  BPagination,
  BSpinner,
} from 'bootstrap-vue'
import Datepicker from 'vuejs-datepicker'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'DoctorBooking',
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BTable,
    BButton,
    BButtonGroup,
    BFormGroup,
    BFormSelect,
    BPagination,
    BSpinner,
    Datepicker,
  },
  data() {
    return {
      loading: true,
      bookings: [],
      dataCount: 0,
      page: 1,
      take: 10,
      takeOptions: [10, 15, 25, 50],
      filterDate: null,
      updatingStatusId: null,
      viewMode: localStorage.getItem('doctorBookingView') || 'cards',
      isMounted: false,
    }
  },
  computed: {
    bookingColumns() {
      return [
        { key: 'patient', label: this.$t('booking.patient_card') },
        { key: 'doctor', label: this.$t('booking.doctor') },
        { key: 'consultation_date', label: this.$t('booking.consultation_date') },
        { key: 'status', label: this.$t('booking.status') },
        { key: 'actions', label: this.$t('common.actions') },
      ]
    },
    statusSelectOptions() {
      return [
        { value: 'pending', text: this.$t('booking.status_pending') },
        { value: 'completed', text: this.$t('booking.status_completed') },
        { value: 'cancelled', text: this.$t('booking.status_cancelled') },
      ]
    },
    canShowPatientCard() {
      return this.$store.state.app.permissions.includes('show-patientCard')
    },
    paginationSummary() {
      if (!this.dataCount) return `${this.$t('common.total')}: 0`
      const from = (this.page - 1) * this.take + 1
      const to = Math.min(this.page * this.take, this.dataCount)
      return `${from}–${to} / ${this.dataCount}`
    },
    statusSummary() {
      const counts = { pending: 0, completed: 0, cancelled: 0 }
      this.bookings.forEach(b => {
        if (counts[b.status] !== undefined) counts[b.status]++
      })
      return [
        { key: 'pending', label: this.$t('booking.status_pending'), count: counts.pending },
        { key: 'completed', label: this.$t('booking.status_completed'), count: counts.completed },
        { key: 'cancelled', label: this.$t('booking.status_cancelled'), count: counts.cancelled },
      ]
    },
  },
  watch: {
    page() {
      if (this.isMounted) this.loadBookings()
    },
    filterDate() {
      if (!this.isMounted) return
      const wasFirstPage = this.page === 1
      this.page = 1
      if (wasFirstPage) this.loadBookings()
    },
    viewMode(newVal) {
      localStorage.setItem('doctorBookingView', newVal)
    },
  },
  mounted() {
    this.loadBookings()
    this.$nextTick(() => { this.isMounted = true })
  },
  methods: {
    formatDate(wantedDate) {
      if (!wantedDate) return ''
      const date = new Date(wantedDate)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    getPatientCardName(option) {
      return option?.patient?.name || '—'
    },
    getDoctorName(option) {
      return option?.full_name || option?.name || '—'
    },
    formatConsultationDateDisplay(value) {
      if (!value) return '—'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '—'
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    getInitials(name) {
      if (!name || name === '—') return '?'
      return name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map(w => w[0].toUpperCase())
        .join('')
    },
    statusLabel(status) {
      const map = {
        pending: this.$t('booking.status_pending'),
        completed: this.$t('booking.status_completed'),
        cancelled: this.$t('booking.status_cancelled'),
      }
      return map[status] || status
    },
    statusButtonVariant(status) {
      const map = {
        pending: 'outline-warning',
        completed: 'outline-success',
        cancelled: 'outline-danger',
      }
      return map[status] || 'outline-primary'
    },
    buildBookingsUrl() {
      let url = `/doctor/auth/my-bookings?page=${this.page}&take=${this.take}&sort=-id&include=patient_card,doctor,services`
      if (this.filterDate) {
        url += `&filter[created_at]=${this.formatDate(this.filterDate)}`
      }
      return url
    },
    normalizeBookingsResponse() {
      if (this.all_data?.success === false && this.all_data?.message) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: { title: this.$t('common.error'), variant: 'danger', text: this.all_data.message },
        })
        this.bookings = []
        this.dataCount = 0
        return
      }
      if (this.all_data?.data?.data && Array.isArray(this.all_data.data.data)) {
        this.dataCount = this.all_data.data.total || 0
        this.bookings = this.all_data.data.data
      } else if (Array.isArray(this.all_data?.data)) {
        this.dataCount = this.all_data.meta?.total ?? this.all_data.total ?? this.all_data.data.length
        this.bookings = this.all_data.data
      } else {
        this.dataCount = this.all_data?.meta?.total ?? this.all_data?.total ?? 0
        this.bookings = this.all_data?.data || []
      }
    },
    async loadBookings() {
      this.loading = true
      try {
        await this.getList(this.buildBookingsUrl())
        this.normalizeBookingsResponse()
      } catch (error) {
        let errorMessage = this.$t('common.error-fetching-data')
        if (error.response?.data?.message) errorMessage = error.response.data.message
        else if (error.response?.data?.error) errorMessage = error.response.data.error
        else if (error.message) errorMessage = error.message
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: { title: this.$t('common.error'), variant: 'danger', text: errorMessage },
        })
        this.bookings = []
        this.dataCount = 0
      } finally {
        this.loading = false
      }
    },
    onTakeChange() {
      const wasAlreadyFirstPage = this.page === 1
      this.page = 1
      if (wasAlreadyFirstPage) this.loadBookings()
    },
    patientCardNavParams(item) {
      const pc = item?.patient_card
      if (!pc?.id) return null
      const patientId = pc.patient?.id ?? pc.patient_id
      if (patientId == null || patientId === '') return null
      return {
        id: String(patientId),
        is_active: pc.is_active === true || pc.is_active === 1 || pc.is_active === '1',
        card_id: String(pc.id),
      }
    },
    goToPatientCard(item) {
      const params = this.patientCardNavParams(item)
      if (!params) return
      this.$router.push({ name: 'active-patient-card', params })
    },
    async updateStatus(item) {
      this.updatingStatusId = item.id
      try {
        await this.$store.dispatch('put', {
          path: `/consultation-bookings/${item.id}`,
          form: { status: item.status },
        })
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: { title: this.$t('common.success'), variant: 'success', text: this.$t('booking.status_updated') },
        })
      } catch {
        await this.loadBookings()
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: { title: this.$t('common.error'), variant: 'danger', text: this.$t('booking.error_generic') },
        })
      } finally {
        this.updatingStatusId = null
      }
    },
  },
}
</script>

<style scoped lang="scss">

/* ── Page wrapper ── */
.doctor-booking-page {
  padding: 0.5rem;
}

.main-card {
  border: none;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  overflow: hidden;
}

.page-container {
  padding: 1.25rem 1.5rem;
}

/* ── Header ── */
.header-row {
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #f0f0f0;
}

.page-title-block {
  display: flex;
  align-items: center;
  gap: 10px;
}

.title-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: #eef2ff;
  border-radius: 8px;
  color: #5a67d8;
}

.page-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #1a1a2e;
}

.toggle-btn {
  font-size: 0.8rem;
  padding: 0.3rem 0.75rem;
}

/* ── Filters ── */
.filters-row {
  margin-bottom: 1rem;
}

.filter-form-group {
  margin-bottom: 0;

  label {
    font-size: 0.8rem;
    font-weight: 600;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 4px;
  }
}

/* ── Status summary chips ── */
.status-summary {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.summary-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  padding: 3px 10px;
  border-radius: 20px;
  font-weight: 500;

  strong {
    font-weight: 700;
  }

  &--pending {
    background: #FAEEDA;
    color: #854F0B;
    border: 1px solid #FAC775;
  }

  &--completed {
    background: #E1F5EE;
    color: #0F6E56;
    border: 1px solid #9FE1CB;
  }

  &--cancelled {
    background: #FCEBEB;
    color: #A32D2D;
    border: 1px solid #F7C1C1;
  }
}

/* ── Loading ── */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3rem 0;
  gap: 1rem;
}

.loading-text {
  color: #999;
  font-size: 0.9rem;
  margin: 0;
}

/* ── Table ── */
.booking-table {
  font-size: 0.875rem;

  thead th {
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #888;
    background: #f8f9fb;
    border-bottom: 2px solid #eee;
    padding: 0.75rem 1rem;
  }

  tbody td {
    padding: 0.75rem 1rem;
    vertical-align: middle;
    border-bottom: 1px solid #f5f5f5;
  }
}

.patient-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #eef2ff;
  color: #5a67d8;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.patient-name-text {
  font-weight: 500;
  color: #1a1a2e;
}

.doctor-name-text {
  color: #444;
}

.date-cell {
  display: flex;
  align-items: center;
  font-size: 0.82rem;
  color: #555;
}

/* Status pill for table */
.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: 20px;

  .status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
  }

  &--pending {
    background: #FAEEDA;
    color: #854F0B;
    .status-dot { background: #EF9F27; }
  }

  &--completed {
    background: #E1F5EE;
    color: #0F6E56;
    .status-dot { background: #1D9E75; }
  }

  &--cancelled {
    background: #FCEBEB;
    color: #A32D2D;
    .status-dot { background: #E24B4A; }
  }
}

/* ── Cards container ── */
.cards-container {
  min-height: 150px;
}

.card-col {
  padding: 0.4rem;
}

/* ── Booking card ── */
.booking-card {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e8e8e8;
  overflow: hidden;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;

  &:hover {
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.09);
    transform: translateY(-2px);
  }

  /* Status variants */
  &--pending {
    border-left: 4px solid #EF9F27;
    background: linear-gradient(to bottom right, #fffbf4, #fff);

    .card-accent-bar { background: linear-gradient(90deg, #EF9F27, #FAC775); }
  }

  &--completed {
    border-left: 4px solid #1D9E75;
    background: linear-gradient(to bottom right, #f4fdf9, #fff);

    .card-accent-bar { background: linear-gradient(90deg, #1D9E75, #5DCAA5); }
  }

  &--cancelled {
    border-left: 4px solid #E24B4A;
    background: linear-gradient(to bottom right, #fff5f5, #fff);
    opacity: 0.92;

    .card-accent-bar { background: linear-gradient(90deg, #E24B4A, #F09595); }
  }
}

/* Top accent bar (thin gradient stripe at top) */
.card-accent-bar {
  height: 3px;
  width: 100%;
}

/* ── Card header ── */
.booking-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px 14px 10px;
  border-bottom: 1px solid #f2f2f2;
  gap: 8px;
}

.booking-avatar {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &--pending {
    background: #FAEEDA;
    color: #854F0B;
  }

  &--completed {
    background: #E1F5EE;
    color: #0F6E56;
  }

  &--cancelled {
    background: #FCEBEB;
    color: #A32D2D;
  }
}

.booking-patient-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1a1a2e;
  line-height: 1.2;
}

.booking-card-id {
  font-size: 0.7rem;
  color: #aaa;
  margin-top: 2px;
}

/* Status badge in card */
.booking-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 20px;
  white-space: nowrap;
  flex-shrink: 0;

  .badge-dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
  }

  &.badge--pending {
    background: #FAC775;
    color: #633806;
    .badge-dot { background: #854F0B; }
  }

  &.badge--completed {
    background: #9FE1CB;
    color: #04342C;
    .badge-dot { background: #085041; }
  }

  &.badge--cancelled {
    background: #F7C1C1;
    color: #501313;
    .badge-dot { background: #791F1F; }
  }
}

/* ── Card body ── */
.booking-card-body {
  padding: 10px 14px;
  flex: 1;
}

.info-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  flex-wrap: wrap;
  font-size: 0.82rem;

  &.status-select-row {
    align-items: center;
  }
}

.info-icon {
  color: #bbb;
  flex-shrink: 0;
}

.info-label {
  font-weight: 600;
  color: #888;
  flex-shrink: 0;
}

.info-value {
  color: #333;
  word-break: break-word;
}

.status-select {
  min-width: 120px;
  font-size: 0.8rem;
}

/* ── Card footer ── */
.booking-card-footer {
  padding: 8px 14px 12px;
  border-top: 1px solid #f2f2f2;
}

.card-action-btn {
  font-size: 0.8rem;
  border-radius: 8px;
  font-weight: 600;
}

.no-card-placeholder {
  text-align: center;
  color: #ccc;
  font-size: 0.85rem;
  padding: 4px 0;
}

/* ── Empty state ── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3rem 1rem;
  text-align: center;
}

.empty-icon-wrap {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #f0f4ff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a0aec0;
  margin-bottom: 1rem;
}

.empty-title {
  font-weight: 600;
  color: #444;
  margin-bottom: 0.5rem;
}

.empty-sub {
  font-size: 0.875rem;
}

/* ── Pagination ── */
.pagination-row {
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid #f0f0f0;
}

.pagination-info {
  min-width: 100px;
}

/* ── Content row ── */
.content-row {
  margin-bottom: 0.5rem;
}
</style>
